import { useEffect, useRef, useCallback } from 'react';

// Global audio cache to reuse Audio objects
const audioCache = new Map<string, HTMLAudioElement>();

// Cleanup function to clear old audio objects
const cleanupAudioCache = () => {
  audioCache.forEach((audio, src) => {
    audio.pause();
    audio.src = '';
    audio.load(); // Reset audio element
  });
  audioCache.clear();
};

const useAudio = (src: string) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Check if it's already a full URL or make it a public path
    const audioSrc = src.startsWith('/') || src.startsWith('http') ? src : `/audio/${src}`;

    // Get or create cached audio object
    if (!audioCache.has(audioSrc)) {
      const audio = new Audio(audioSrc);
      audio.preload = 'auto';
      audioCache.set(audioSrc, audio);
    }

    audioRef.current = audioCache.get(audioSrc)!;

    // No cleanup - let audio continue playing even if component unmounts
  }, [src]);

  const play = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (audioRef.current) {
        const audio = audioRef.current;
        audio.currentTime = 0; // Reset to start

        const handleEnded = () => {
          audio.removeEventListener('ended', handleEnded);
          audio.removeEventListener('error', handleError);
          resolve();
        };

        const handleError = () => {
          audio.removeEventListener('ended', handleEnded);
          audio.removeEventListener('error', handleError);
          resolve();
        };

        audio.addEventListener('ended', handleEnded, { once: true });
        audio.addEventListener('error', handleError, { once: true });

        audio.play().catch(() => {
          // Silently handle audio play errors
          audio.removeEventListener('ended', handleEnded);
          audio.removeEventListener('error', handleError);
          resolve();
        });
      } else {
        resolve();
      }
    });
  }, []);

  return play;
};

// Export cleanup function for global cleanup if needed
export { cleanupAudioCache };
export default useAudio;